import type { PublicClassesOutput, PublicClassGuideOutput } from '../public-classes.js';
import type { PublicChampionshipsOutput } from '../public-championships.js';
import type { PublicAtlasOutput } from '../public-atlas.js';
import type { PublicPageMetadataOutput } from '../public-page-metadata.js';
import type { TSeriesMatchingRequest, TSeriesMatchingResponse, TSeriesMatchingPreview, TSeriesMatchingApplyRequest, TSeriesMatchingResult } from '../series-matching.js';
import type { TCatalogRefreshRequest, TCatalogRefreshPreview, TWpciviMappingRequest, TWpciviMappingPreview, TWpciviClassMappingFile } from '../index';
import type { TModelDefaultsPreview } from '../index';
import type { TModelReconciliationRequest, TModelReconciliationInventory, TModelReconciliationPreview, TModelReconciliationResult } from '../model-reconciliation.js';
import type { SeriesPublicationStatusOutput, SeriesPublicationPreviewOutput, SeriesPublicationReceiptOutput, PublicSeriesTrendsOutput, PublicSeriesReportOutput } from '../series-publication.js';
import type { SeriesReportSelection, SeriesReportOutput, SeriesReportIdentitiesOutput } from '../series-report.js';
import type { ShowResultsReleaseStatusOutput, ShowResultsReleasePreviewOutput, ShowResultsReleaseReceiptOutput, PublicShowResultsOutput, PublicSeriesResultsOutput } from '../show-results-release.js';
import type { SeriesIdentityAssignmentsOutput, TShowSeries, CreateShowSeriesInput, UpdateShowSeriesInput, ListShowSeriesOutput, ListSeriesEditionsOutput, SeriesAlignmentQueryInput, SeriesAlignmentOutput, SeriesSuggestionsQueryInput, SeriesSuggestionsOutput, UpdateSeriesAlignmentInput, SeriesAlignmentEditionOutput, SeriesIdentityOutput, CreateSeriesIdentityInput, UpdateSeriesIdentityInput } from "../index";
// Manually maintained transport and endpoint methods. Only api-types.ts is generated
// by npm run generate:api-types; keep methods aligned with handlers, Zod and OpenAPI.
import type {
  CreateOrganizationInput,
  TOrganization,
  CreateShowInput,
  TShow,
  ListOrganizationsOutput,
  CreateDivisionsInput,
  TDivision,
  TClass,
  CreateExhibitorInput,
  TExhibitor,
  ListExhibitorsOutput,
  UpdateShowExhibitorInput,
  UpdateShowExhibitorOutput,
  CreateEntryInput,
  TEntry,
  UpdateEntryInput,
  UpdateEntryOutput,
  MarkEntryNotEnteredInput,
  MarkEntryNotEnteredOutput,
  JudgingClassMoveInput,
  JudgingClassMoveOutput,
  CheckInInput,
  TExhibit,
  GetEntryOutput,
  UpdateShowEntrantFinalizationInput,
  UpdateShowEntrantFinalizationOutput,
  SubmitScoresInput,
  TScore,
  FinalizeAwardsInput,
  TAward,
  ClassPlacementsByEntryCodesInput,
  ClassPlacementsByEntryCodesOutput,
  TUpdateOrganizationInput,
  UpdateShowInput,
  ListShowsOutput,
  CreateClubInput,
  BulkCreateClubsInput,
  UpdateClubInput,
  TClub,
  ListClubsOutput,
  BulkCreateClubsOutput,
  GetShowExhibitorByPersonOutput,
  ListCatalogCategoriesOutput,
  ListCatalogClassesOutput,
  AddCatalogClassToShowInput,
  AddCatalogClassToShowOutput,
  CatalogClassUsageOutput,
  CreateCatalogClassInput,
  CreateCatalogSubtypeInput,
  UpdateCatalogSubtypeInput,
  UpdateCatalogClassInput,
  CatalogClassOutput,
  UpdateShowClassInput,
  UpdateShowClassOutput,
  UpdateClassFinalizationInput,
  UpdateClassFinalizationOutput,
  CreatePersonInput,
  UpdatePersonInput,
  UpdatePersonMembershipInput,
  ListPeopleOutput,
  TPerson,
  MergePeopleInput,
  MergePeopleOutput,
  MergePeoplePreviewInput,
  MergePeoplePreviewOutput,
  PersonParticipationOutput,
  PersonParticipationEntriesOutput,
  CreateShowRoleAssignmentInput,
  UpdateShowRoleAssignmentInput,
  ListShowRoleAssignmentsOutput,
  ListShowJudgesOutput,
  UpdateClassJudgesInput,
  TShowRoleAssignment,
  ReindexClassCodesInput,
  ReindexClassCodesOutput,
  LeaderboardOutput,
  EntrantPacketInput,
  EntrantPacketDownload,
  AllocateIdentifiersInput,
  AllocateIdentifiersOutput,
  IdentifierIntakeContextInput,
  IdentifierIntakeContextOutput,
  IdentifierAvailabilityOutput,
  ReportSummaryOutput,
  ReportResultsOutput,
  ReportAwardsOutput,
  ReportClubParticipationOutput,
  ReportAwardCardsOutput,
  ReportAwardsEmailPreviewOutput,
  ShowStatsOutput,
  ShowStatsComparisonOutput,
  SendReportAwardsEmailInput,
  SendReportAwardsEmailOutput,
  ShowSpecialAwardsOutput,
  HeroAwardStateOutput,
  SetBestInShowInput,
  SetBestInDivisionInput,
  SetChampionAwardsInput,
} from "../index";

export class ApiError extends Error {
  readonly payload: Record<string, unknown> | null;

  constructor(readonly status: number, readonly rawBody: string, readonly retryAfterMs?: number) {
    super(`API request failed [${status}] ${rawBody.trim() || "No response body"}`);
    this.name = "ApiError";
    let payload: unknown;
    try { payload = JSON.parse(rawBody); } catch { payload = null; }
    this.payload = payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload as Record<string, unknown> : null;
  }
}

export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let body: string;
  try { body = await response.text(); } catch { body = response.statusText ?? ""; }
  const retryAfter = response.headers.get('retry-after');
  const seconds = retryAfter?.trim() ? Number(retryAfter) : NaN;
  const date = retryAfter ? Date.parse(retryAfter) : NaN;
  const delay = Number.isFinite(seconds) && seconds >= 0
    ? seconds * 1000
    : Number.isFinite(date) ? Math.max(0, date - Date.now()) : undefined;
  return new ApiError(response.status, body, delay);
}

/** `readOnly` marks a non-GET request that changes nothing, so a fetch wrapper need not treat it as a write. */
export type ApiRequestInit = RequestInit & { readOnly?: boolean };

export interface ApiClientConfig {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  defaultHeaders?: HeadersInit;
}

export class ApiClient {
  async matchShowPeople(showId: string, q: string): Promise<import('../person-contact.js').TPersonMatches> {
    return this.request(`/api/shows/${showId}/people/matches?q=${encodeURIComponent(q)}`, { method: 'GET' });
  }
  async matchShowStaffPeople(showId: string, q: string): Promise<import('../person-contact.js').TStaffPersonMatches> {
    return this.request(`/api/shows/${showId}/staff/people/matches?q=${encodeURIComponent(q)}`, { method: 'GET' });
  }
  async selectShowPerson(showId: string, matchProof: string): Promise<import('../person-contact.js').TPersonContactResponse> {
    return this.request(`/api/shows/${showId}/people/selections`, { method: 'POST', body: JSON.stringify({ matchProof }) });
  }
  async getShowPersonContact(showId: string, personId: string, selectionProof?: string): Promise<import('../person-contact.js').TPersonContactResponse> {
    return this.request(`/api/shows/${showId}/people/${personId}/contact`, { method: 'GET', ...(selectionProof ? { headers: { 'X-Person-Selection': selectionProof } } : {}) });
  }
  async updateShowPersonContact(showId: string, personId: string, payload: import('../person-contact.js').TUpdateContactRequest): Promise<import('../person-contact.js').TPersonContactResponse> {
    return this.request(`/api/shows/${showId}/people/${personId}/contact`, { method: 'PATCH', body: JSON.stringify(payload) });
  }
  async createShowEntrant(showId: string, payload: import('../person-contact.js').TCreateShowEntrantRequest): Promise<import('../index.js').GetShowExhibitorByPersonOutput> {
    return this.request(`/api/shows/${showId}/entrants`, { method: 'POST', body: JSON.stringify(payload) });
  }
  async getContactCorrectionAuthority(personId: string): Promise<{ fields: import('../person-contact.js').TContactField[]; reviewToken: string }> {
    return this.request(`/api/people/${personId}/contact-authority`, { method: 'GET' });
  }
  async releaseContactCorrections(personId: string, fields: import('../person-contact.js').TContactField[], reviewToken: string): Promise<{ fields: string[] }> {
    return this.request(`/api/people/${personId}/contact-authority`, { method: 'PATCH', body: JSON.stringify({ fields, reviewToken }) });
  }
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly defaultHeaders: HeadersInit | undefined;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, "");
    this.fetchImpl = config.fetchImpl ?? ((input, init) => fetch(input, init));
    this.defaultHeaders = config.defaultHeaders;
  }

  private async request<T>(path: string, init: ApiRequestInit): Promise<T> {
    let res: Response;
    try {
      const headers = new Headers(this.defaultHeaders ?? undefined);
      if (init.headers) {
        new Headers(init.headers).forEach((value, key) => headers.set(key, value));
      }
      if (init.body !== undefined && !headers.has('content-type')) {
        headers.set('content-type', 'application/json');
      }

      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        headers,
      });
    } catch (error) {
      const details = error instanceof Error ? error.message : String(error);
      throw new Error(`Unable to reach API at ${this.baseUrl}: ${details}`);
    }

    if (!res.ok) throw await apiErrorFromResponse(res);

    if (res.status === 204) return undefined as unknown as T;
    return (await res.json()) as T;
  }

  createOrganization(payload: CreateOrganizationInput) {
    return this.request<{ organization: TOrganization }>(`/api/orgs`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  updateOrganization(orgId: string, payload: TUpdateOrganizationInput) {
    return this.request<{ organization: TOrganization }>(`/api/orgs/${orgId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  listShowSeries(params: { organizationId?: string; cursor?: string } = {}) {
    const query = new URLSearchParams(Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined));
    return this.request<ListShowSeriesOutput>(`/api/show-series?${query}`, { method: "GET" });
  }
  getShowSeries(seriesId: string) {
    return this.request<{ series: TShowSeries }>(`/api/show-series/${seriesId}`, { method: "GET" });
  }
  createShowSeries(payload: CreateShowSeriesInput) {
    return this.request<{ series: TShowSeries }>(`/api/show-series`, { method: "POST", body: JSON.stringify(payload) });
  }
  updateShowSeries(seriesId: string, payload: UpdateShowSeriesInput) {
    return this.request<{ series: TShowSeries }>(`/api/show-series/${seriesId}`, { method: "PATCH", body: JSON.stringify(payload) });
  }
  listSeriesEditions(seriesId: string, cursor?: string) {
    return this.request<ListSeriesEditionsOutput>(`/api/show-series/${seriesId}/editions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, { method: "GET" });
  }

  createShow(payload: CreateShowInput) {
    return this.request<{ show: TShow }>(`/api/shows`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getSeriesPublication(showId: string, signal?: AbortSignal) {
    return this.request<SeriesPublicationStatusOutput>(`/api/shows/${showId}/series-publication`, { method: 'GET', signal, cache: 'no-store' });
  }
  previewSeriesPublication(showId: string) {
    return this.request<SeriesPublicationPreviewOutput>(`/api/shows/${showId}/series-publication/preview`, { method: 'POST', body: '{}' });
  }
  publishSeriesEdition(showId: string, token: string, acknowledgeIncompleteAlignment: boolean) {
    return this.request<SeriesPublicationReceiptOutput>(`/api/shows/${showId}/series-publication`, { method: 'POST', body: JSON.stringify({ token, acknowledgeIncompleteAlignment }) });
  }
  unpublishSeriesEdition(showId: string, expectedGeneration: number) {
    return this.request<SeriesPublicationReceiptOutput>(`/api/shows/${showId}/series-publication`, { method: 'DELETE', body: JSON.stringify({ expectedGeneration }) });
  }
  getShowResultsRelease(showId: string, signal?: AbortSignal) {
    return this.request<ShowResultsReleaseStatusOutput>(`/api/shows/${showId}/results-release`, { method: 'GET', signal, cache: 'no-store' });
  }
  previewShowResultsRelease(showId: string) {
    return this.request<ShowResultsReleasePreviewOutput>(`/api/shows/${showId}/results-release/preview`, { method: 'POST', body: '{}' });
  }
  releaseShowResults(showId: string, token: string) {
    return this.request<ShowResultsReleaseReceiptOutput>(`/api/shows/${showId}/results-release`, { method: 'POST', body: JSON.stringify({ token }) });
  }
  withdrawShowResults(showId: string, expectedGeneration: number) {
    return this.request<ShowResultsReleaseReceiptOutput>(`/api/shows/${showId}/results-release`, { method: 'DELETE', body: JSON.stringify({ expectedGeneration }) });
  }
  getPublicClasses(signal?: AbortSignal) {
    return this.request<PublicClassesOutput>('/api/public/classes', { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicClassGuide(classKey: string, signal?: AbortSignal) {
    return this.request<PublicClassGuideOutput>('/api/public/classes/' + encodeURIComponent(classKey), { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicAtlas(signal?: AbortSignal) {
    return this.request<PublicAtlasOutput>('/api/public/shows', { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicPageMetadata(path: string, signal?: AbortSignal) {
    return this.request<PublicPageMetadataOutput>('/api/public/page-metadata?' + new URLSearchParams({ path }), { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicShowResults(slug: string, signal?: AbortSignal) {
    return this.request<PublicShowResultsOutput>(`/api/public/shows/${encodeURIComponent(slug)}`, { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicSeriesResults(slug: string, signal?: AbortSignal) {
    return this.request<PublicSeriesResultsOutput>(`/api/public/show-series/${encodeURIComponent(slug)}/results`, { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicChampionships(slug: string, selection: { fromYear?: number; toYear?: number }, signal?: AbortSignal) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(selection)) if (value !== undefined) query.set(key, String(value));
    return this.request<PublicChampionshipsOutput>(`/api/public/show-series/${encodeURIComponent(slug)}/championships?${query}`, { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicSeriesTrends(slug: string, selection: { fromYear?: number; toYear?: number; cursor?: string; limit?: number }, signal?: AbortSignal) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(selection)) if (value !== undefined) query.set(key, String(value));
    return this.request<PublicSeriesTrendsOutput>(`/api/public/show-series/${encodeURIComponent(slug)}/trends?${query}`, { method: 'GET', signal, cache: 'no-store' });
  }
  getPublicSeriesReport(slug: string, selection: { fromYear: number; toYear: number; trendKeys: string[] }, signal?: AbortSignal) {
    const query = new URLSearchParams({ fromYear: String(selection.fromYear), toYear: String(selection.toYear), trendKeys: selection.trendKeys.join(',') });
    return this.request<PublicSeriesReportOutput>(`/api/public/show-series/${encodeURIComponent(slug)}/report?${query}`, { method: 'GET', signal, cache: 'no-store' });
  }
  getSeriesReport(seriesId: string, selection: SeriesReportSelection, signal?: AbortSignal) {
    const query = new URLSearchParams({ fromYear: String(selection.fromYear), toYear: String(selection.toYear) });
    if (selection.identityIds?.length) query.set('identityIds', selection.identityIds.join(','));
    return this.request<SeriesReportOutput>(`/api/show-series/${seriesId}/report?${query}`, { method: 'GET', signal });
  }
  listSeriesReportIdentities(seriesId: string, cursor?: string, signal?: AbortSignal) {
    const query = new URLSearchParams();
    if (cursor) query.set('cursor', cursor);
    return this.request<SeriesReportIdentitiesOutput>(`/api/show-series/${seriesId}/identities?${query}`, { method: 'GET', signal });
  }
  getSeriesMatching(seriesId: string, years?: string, signal?: AbortSignal) {
    const query = new URLSearchParams(); if (years) query.set('years', years);
    return this.request<TSeriesMatchingResponse>(`/api/show-series/${seriesId}/matching?${query}`, {method:'GET',signal});
  }
  previewSeriesMatching(seriesId: string, payload: TSeriesMatchingRequest) {
    return this.request<TSeriesMatchingPreview>(`/api/show-series/${seriesId}/matching/preview`, {method:'POST',body:JSON.stringify(payload)});
  }
  applySeriesMatching(seriesId: string, payload: TSeriesMatchingApplyRequest) {
    return this.request<TSeriesMatchingResult>(`/api/show-series/${seriesId}/matching/apply`, {method:'POST',body:JSON.stringify(payload)});
  }
  getSeriesAlignment(seriesId: string, params: Partial<SeriesAlignmentQueryInput> = {}, signal?: AbortSignal) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined) query.set(key, String(value));
    return this.request<SeriesAlignmentOutput>(`/api/show-series/${seriesId}/alignment?${query}`, { method: 'GET', signal });
  }

  getSeriesIdentityAssignments(seriesId: string, params: { identityId: string; cursor?: string; limit?: number }, signal?: AbortSignal) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined) query.set(key, String(value));
    return this.request<SeriesIdentityAssignmentsOutput>(`/api/show-series/${seriesId}/identity-assignments?${query}`, { method: 'GET', signal });
  }

  getSeriesSuggestions(seriesId: string, params: Partial<SeriesSuggestionsQueryInput> & { targetShowId: string }, signal?: AbortSignal) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined) query.set(key, String(value));
    return this.request<SeriesSuggestionsOutput>(`/api/show-series/${seriesId}/suggestions?${query}`, { method: 'GET', signal });
  }

  updateSeriesAlignment(seriesId: string, payload: UpdateSeriesAlignmentInput) {
    return this.request<{ editions: SeriesAlignmentEditionOutput[] }>(`/api/show-series/${seriesId}/alignment`, { method: 'PUT', body: JSON.stringify(payload) });
  }

  reconcileSeriesAlignment(seriesId: string, payload: { showIds: string[]; expectedRevision: string }) {
    return this.request<{ assignedCount: number }>(`/api/show-series/${seriesId}/alignment/reconcile`, { method: 'POST', body: JSON.stringify(payload) });
  }

  createSeriesIdentity(seriesId: string, payload: CreateSeriesIdentityInput) {
    return this.request<{ identity: SeriesIdentityOutput }>(`/api/show-series/${seriesId}/identities`, { method: 'POST', body: JSON.stringify(payload) });
  }

  updateSeriesIdentity(seriesId: string, identityId: string, payload: UpdateSeriesIdentityInput) {
    return this.request<{ identity: SeriesIdentityOutput }>(`/api/show-series/${seriesId}/identities/${identityId}`, { method: 'PATCH', body: JSON.stringify(payload) });
  }

  deleteSeriesIdentity(seriesId: string, identityId: string, expectedRevision: number) {
    return this.request<void>(`/api/show-series/${seriesId}/identities/${identityId}`, { method: 'DELETE', body: JSON.stringify({ expectedRevision }) });
  }

  previewCatalogRefresh(payload: TCatalogRefreshRequest) {
    return this.request<TCatalogRefreshPreview>('/api/catalog/refresh/preview', { method: 'POST', body: JSON.stringify(payload) });
  }
  getModelReconciliation() {
    return this.request<TModelReconciliationInventory>('/api/catalog/reconciliation', { method: 'GET' });
  }
  previewModelReconciliation(payload: TModelReconciliationRequest) {
    return this.request<TModelReconciliationPreview>('/api/catalog/reconciliation/preview', { method: 'POST', body: JSON.stringify(payload) });
  }
  applyModelReconciliation(payload: TModelReconciliationRequest & { reviewToken: string; reviewedAt: string }) {
    return this.request<TModelReconciliationResult>('/api/catalog/reconciliation/apply', { method: 'POST', body: JSON.stringify(payload) });
  }
  applyCatalogRefresh(payload: TCatalogRefreshRequest & { reviewToken: string }) {
    return this.request<{ receiptId: string; sourceDigest: string; catalogRevision: number; appliedKeys: string[]; alreadyApplied: boolean }>('/api/catalog/refresh/apply', { method: 'POST', body: JSON.stringify(payload) });
  }
  previewWpciviClassMappings(payload: TWpciviMappingRequest) {
    return this.request<TWpciviMappingPreview>('/api/catalog/import-mappings/preview', { method: 'POST', body: JSON.stringify(payload) });
  }
  exportWpciviClassMappings(payload: TWpciviMappingRequest & { reviewToken: string }) {
    return this.request<TWpciviClassMappingFile>('/api/catalog/import-mappings/export', { method: 'POST', body: JSON.stringify(payload) });
  }

  seedCatalog() {
    return this.request<{ categories: number; classes: number }>(`/api/catalog/seed`, {
      method: "POST",
    });
  }

  listCatalogCategories() {
    return this.request<ListCatalogCategoriesOutput>(`/api/catalog/categories`, {
      method: "GET",
    });
  }

  listCatalogClasses(query?: { category?: string; q?: string; flag?: string; includeRetired?: boolean }) {
    const params = new URLSearchParams();
    if (query) {
      if (query.category) params.append("category", query.category);
      if (query.q) params.append("q", query.q);
      if (query.flag) params.append("flag", query.flag);
      if (query.includeRetired !== undefined) params.append("includeRetired", String(query.includeRetired));
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ListCatalogClassesOutput>(`/api/catalog/classes${suffix}`, {
      method: "GET",
    });
  }

  getCatalogClass(catalogClassId: string) {
    return this.request<CatalogClassOutput>(`/api/catalog/classes/${catalogClassId}`, {
      method: "GET",
    });
  }

  listCatalogClassUsage(catalogClassId: string) {
    return this.request<CatalogClassUsageOutput>(
      `/api/catalog/classes/${catalogClassId}/shows`,
      {
        method: "GET",
      },
    );
  }

  createCatalogClass(payload: CreateCatalogClassInput) {
    return this.request<CatalogClassOutput>(`/api/catalog/classes`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  updateCatalogClass(catalogClassId: string, payload: UpdateCatalogClassInput) {
    return this.request<CatalogClassOutput>(`/api/catalog/classes/${catalogClassId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  createCatalogSubtype(catalogClassId: string, payload: CreateCatalogSubtypeInput) {
    return this.request<CatalogClassOutput>(`/api/catalog/classes/${catalogClassId}/subtypes`, { method: "POST", body: JSON.stringify(payload) });
  }

  updateCatalogSubtype(catalogClassId: string, subtypeId: string, payload: UpdateCatalogSubtypeInput) {
    return this.request<CatalogClassOutput>(`/api/catalog/classes/${catalogClassId}/subtypes/${subtypeId}`, { method: "PATCH", body: JSON.stringify(payload) });
  }

  updateShow(showId: string, payload: UpdateShowInput) {
    return this.request<{ show: TShow }>(`/api/shows/${showId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  getHeroAward(showId: string) {
    return this.request<HeroAwardStateOutput>(`/api/shows/${showId}/special-awards/hero`, { method: "GET" });
  }

  setHeroAward(showId: string, exhibitId: string | null) {
    return this.request<HeroAwardStateOutput>(`/api/shows/${showId}/special-awards/hero`, {
      method: exhibitId === null ? "DELETE" : "PUT",
      ...(exhibitId === null ? {} : { body: JSON.stringify({ exhibitId }) }),
    });
  }

  getSpecialAwards(showId: string) {
    return this.request<ShowSpecialAwardsOutput>(
      `/api/shows/${showId}/special-awards`,
      {
        method: "GET",
      },
    );
  }

  setBestInShow(showId: string, payload: SetBestInShowInput) {
    return this.request<ShowSpecialAwardsOutput>(
      `/api/shows/${showId}/special-awards/best-in-show`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  }

  clearBestInShow(showId: string) {
    return this.request<ShowSpecialAwardsOutput>(
      `/api/shows/${showId}/special-awards/best-in-show`,
      {
        method: "DELETE",
      },
    );
  }

  setBestInDivision(showId: string, divisionId: string, payload: SetBestInDivisionInput) {
    return this.request<ShowSpecialAwardsOutput>(
      `/api/shows/${showId}/divisions/${divisionId}/best-in-division`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  }

  clearBestInDivision(showId: string, divisionId: string) {
    return this.request<ShowSpecialAwardsOutput>(
      `/api/shows/${showId}/divisions/${divisionId}/best-in-division`,
      {
        method: "DELETE",
      },
    );
  }

  setChampionAwards(showId: string, payload: SetChampionAwardsInput) {
    return this.request<ShowSpecialAwardsOutput>(
      `/api/shows/${showId}/special-awards/champions`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  }

  listPeople(query?: { q?: string; member?: "true" | "false"; page?: number; pageSize?: number }) {
    const params = new URLSearchParams();
    if (query) {
      if (query.q) params.append("q", query.q);
      if (query.member) params.append("member", query.member);
      if (query.page !== undefined) params.append("page", String(query.page));
      if (query.pageSize !== undefined) params.append("pageSize", String(query.pageSize));
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ListPeopleOutput>(`/api/people${suffix}`, {
      method: "GET",
    });
  }

  createPerson(payload: CreatePersonInput) {
    return this.request<{ person: TPerson }>(`/api/people`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getPerson(personId: string) {
    return this.request<{ person: TPerson }>(`/api/people/${personId}`, { method: "GET" });
  }

  updatePerson(personId: string, payload: UpdatePersonInput) {
    return this.request<{ person: TPerson }>(`/api/people/${personId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  updatePersonMembership(personId: string, payload: UpdatePersonMembershipInput) {
    return this.request<{ person: TPerson }>(`/api/people/${personId}/membership`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  mergePerson(personId: string, payload: MergePeopleInput) {
    return this.request<MergePeopleOutput>(`/api/people/${personId}/merge`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  previewMergePerson(personId: string, payload: MergePeoplePreviewInput) {
    return this.request<MergePeoplePreviewOutput>(`/api/people/${personId}/merge/preview`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getPersonParticipation(personId: string, query?: { page?: number; pageSize?: number }) {
    const params = new URLSearchParams();
    if (query?.page !== undefined) params.append("page", String(query.page));
    if (query?.pageSize !== undefined) params.append("pageSize", String(query.pageSize));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<PersonParticipationOutput>(
      `/api/people/${personId}/participation${suffix}`,
      { method: "GET" },
    );
  }

  getPersonParticipationEntries(
    personId: string,
    showId: string,
    query?: { page?: number; pageSize?: number },
  ) {
    const params = new URLSearchParams();
    if (query?.page !== undefined) params.append("page", String(query.page));
    if (query?.pageSize !== undefined) params.append("pageSize", String(query.pageSize));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<PersonParticipationEntriesOutput>(
      `/api/people/${personId}/participation/${showId}/entries${suffix}`,
      { method: "GET" },
    );
  }

  listShowStaff(showId: string) {
    return this.request<ListShowRoleAssignmentsOutput>(`/api/shows/${showId}/staff`, {
      method: "GET",
    });
  }

  listShowJudges(showId: string) {
    return this.request<ListShowJudgesOutput>(`/api/shows/${showId}/judges`, {
      method: "GET",
    });
  }

  allocateIdentifiers(showId: string, payload?: AllocateIdentifiersInput) {
    return this.request<AllocateIdentifiersOutput>(`/api/shows/${showId}/identifiers/allocate`, {
      method: "POST",
      body: payload ? JSON.stringify(payload) : undefined,
    });
  }

  identifierIntakeContext(showId: string, payload: IdentifierIntakeContextInput) {
    return this.request<IdentifierIntakeContextOutput>(
      `/api/shows/${showId}/identifiers/intake-context`,
      { method: "POST", body: JSON.stringify(payload), readOnly: true },
    );
  }

  identifierAvailability(showId: string) {
    return this.request<IdentifierAvailabilityOutput>(
      `/api/shows/${showId}/identifiers/availability`,
      {
        method: "GET",
      },
    );
  }

  addShowStaff(showId: string, payload: CreateShowRoleAssignmentInput) {
    return this.request<{ assignment: TShowRoleAssignment }>(`/api/shows/${showId}/staff`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  updateShowStaff(showId: string, assignmentId: string, payload: UpdateShowRoleAssignmentInput) {
    return this.request<{ assignment: TShowRoleAssignment }>(
      `/api/shows/${showId}/staff/${assignmentId}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  }

  removeShowStaff(showId: string, assignmentId: string) {
    return this.request<void>(`/api/shows/${showId}/staff/${assignmentId}`, {
      method: "DELETE",
    });
  }

  listShows(query?: Record<string, string | number | undefined>) {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ListShowsOutput>(`/api/shows${suffix}`, {
      method: "GET",
    });
  }

  lookupShow(params: ({ slug: string; showId?: never } | { showId: string; slug?: never }) & { orgId?: string }) {
    const search = new URLSearchParams();
    if (params.showId !== undefined) search.set('showId', params.showId);
    else search.set('slug', params.slug);
    if (params.orgId) {
      search.set('orgId', params.orgId);
    }
    const query = search.toString();
    return this.request<{ show: TShow }>(`/api/shows/lookup?${query}`, {
      method: "GET",
    });
  }

  bulkCreateDivisions(showId: string, payload: CreateDivisionsInput) {
    return this.request<{ divisions: Array<TDivision & { classes: TClass[] }> }>(
      `/api/shows/${showId}/classes/bulk`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  addCatalogClassToShow(showId: string, payload: AddCatalogClassToShowInput) {
    return this.request<AddCatalogClassToShowOutput>(
      `/api/shows/${showId}/classes/from-catalog`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  previewClassModelDefaults(classId: string) {
    return this.request<TModelDefaultsPreview>(`/api/classes/${classId}/model-defaults/preview`, { method: 'POST' });
  }
  applyClassModelDefaults(classId: string, reviewToken: string) {
    return this.request<{ class: TClass }>(`/api/classes/${classId}/model-defaults/apply`, { method: 'POST', body: JSON.stringify({ reviewToken }) });
  }

  updateShowClass(classId: string, payload: UpdateShowClassInput) {
    return this.request<UpdateShowClassOutput>(`/api/classes/${classId}/rules`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  updateClassJudges(classId: string, payload: UpdateClassJudgesInput) {
    return this.request<UpdateShowClassOutput>(`/api/classes/${classId}/judges`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  updateClassFinalization(classId: string, payload: UpdateClassFinalizationInput) {
    return this.request<UpdateClassFinalizationOutput>(`/api/classes/${classId}/finalization`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  listDivisions(showId: string) {
    return this.request<{ divisions: Array<TDivision & { classes: TClass[] }> }>(
      `/api/shows/${showId}/classes`,
      {
        method: "GET",
      },
    );
  }

  listClubs(query?: {
    organizationId?: string | null;
    q?: string;
    limit?: number;
    includeGlobal?: boolean;
  }) {
    const params = new URLSearchParams();
    if (query) {
      if (query.organizationId !== undefined) {
        const value = query.organizationId === null ? "null" : query.organizationId;
        params.append("organizationId", value);
      }
      if (query.q) params.append("q", query.q);
      if (typeof query.limit === "number") params.append("limit", String(query.limit));
      if (typeof query.includeGlobal === "boolean") {
        params.append("includeGlobal", String(query.includeGlobal));
      }
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ListClubsOutput>(`/api/clubs${suffix}`, {
      method: "GET",
    });
  }

  bulkCreateClubs(orgId: string, payload: BulkCreateClubsInput) {
    return this.request<BulkCreateClubsOutput>(`/api/orgs/${orgId}/clubs/bulk`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  createClub(payload: CreateClubInput) {
    return this.request<{ club: TClub }>(`/api/clubs`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  updateClub(clubId: string, payload: UpdateClubInput) {
    return this.request<{ club: TClub }>(`/api/clubs/${clubId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  deleteClub(clubId: string) {
    return this.request<void>(`/api/clubs/${clubId}`, {
      method: "DELETE",
    });
  }

  listShowClubs(showId: string, query?: { q?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (query) {
      if (query.q) params.append("q", query.q);
      if (typeof query.limit === "number") params.append("limit", String(query.limit));
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ListClubsOutput>(`/api/shows/${showId}/clubs${suffix}`, {
      method: "GET",
    });
  }

  reindexClassCodes(showId: string, payload?: ReindexClassCodesInput) {
    const init: RequestInit = { method: "POST" };
    if (payload && Object.keys(payload).length > 0) {
      init.body = JSON.stringify(payload);
    }

    return this.request<ReindexClassCodesOutput>(
      `/api/shows/${showId}/classes/reindex-codes`,
      init,
    );
  }

  createExhibitor(payload: CreateExhibitorInput) {
    return this.request<{ exhibitor: TExhibitor }>(`/api/exhibitors`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  updateShowExhibitor(
    showId: string,
    exhibitorId: string,
    payload: UpdateShowExhibitorInput,
  ) {
    return this.request<UpdateShowExhibitorOutput>(
      `/api/shows/${showId}/exhibitors/${exhibitorId}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  }

  deleteShowExhibitor(showId: string, exhibitorId: string) {
    return this.request<void>(`/api/shows/${showId}/exhibitors/${exhibitorId}`, {
      method: "DELETE",
    });
  }

  updateShowEntrantFinalization(
    showId: string,
    exhibitorId: string,
    payload: UpdateShowEntrantFinalizationInput,
  ) {
    return this.request<UpdateShowEntrantFinalizationOutput>(
      `/api/shows/${showId}/entrants/${exhibitorId}/finalization`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  }

  listExhibitors(query?: { showId?: string; q?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (query) {
      if (query.showId) params.append("showId", query.showId);
      if (query.q) params.append("q", query.q);
      if (typeof query.limit === "number") params.append("limit", String(query.limit));
      if (typeof query.offset === "number") params.append("offset", String(query.offset));
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ListExhibitorsOutput>(`/api/exhibitors${suffix}`, {
      method: "GET",
    });
  }

  getShowExhibitorByPerson(showId: string, personId: string) {
    return this.request<GetShowExhibitorByPersonOutput>(
      `/api/shows/${showId}/exhibitors/by-person/${personId}`,
      {
        method: "GET",
      },
    );
  }

  createEntry(payload: CreateEntryInput) {
    return this.request<{ entry: TEntry }>(`/api/entries`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getEntry(entryId: string) {
    return this.request<GetEntryOutput>(`/api/entries/${entryId}`, {
      method: "GET",
    });
  }

  updateEntry(entryId: string, payload: UpdateEntryInput) {
    return this.request<UpdateEntryOutput>(`/api/entries/${entryId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  markEntryNotEntered(entryId: string, payload?: MarkEntryNotEnteredInput) {
    return this.request<MarkEntryNotEnteredOutput>(`/api/entries/${entryId}/not-entered`, {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    });
  }

  deleteEntry(entryId: string) {
    return this.request<void>(`/api/entries/${entryId}`, {
      method: "DELETE",
    });
  }

  checkInEntry(entryId: string, payload: CheckInInput) {
    return this.request<{ exhibit: TExhibit }>(`/api/entries/${entryId}/checkin`, {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    });
  }

  markLabelPrinted(exhibitId: string) {
    return this.request<{ exhibit: TExhibit }>(`/api/exhibits/${exhibitId}/labels/print`, {
      method: "POST",
    });
  }

  startJudging(classId: string) {
    return this.request<{ class: TClass }>(`/api/judging/classes/${classId}/start`, {
      method: "POST",
    });
  }

  submitScores(payload: SubmitScoresInput) {
    return this.request<{ scores: TScore[] }>(`/api/scores/bulk`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  moveEntryClassDuringJudging(payload: JudgingClassMoveInput) {
    return this.request<JudgingClassMoveOutput>(`/api/judging/entries/move-class`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  recordJudgingResultsByEntryCodes(payload: ClassPlacementsByEntryCodesInput) {
    return this.request<ClassPlacementsByEntryCodesOutput>(
      `/api/judging/results/by-entry-codes`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  finalizeAwards(payload: FinalizeAwardsInput) {
    return this.request<{ awards: TAward[]; show: TShow }>(`/api/awards/finalize`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getShowStats(showId: string) {
    return this.request<ShowStatsOutput>(`/api/shows/${showId}/stats`, {
      method: "GET",
    });
  }

  getShowStatsComparison(showId: string) {
    return this.request<ShowStatsComparisonOutput>(`/api/shows/${showId}/stats/comparison`, {
      method: "GET",
    });
  }

  getReportSummary(showId: string) {
    return this.request<ReportSummaryOutput>(`/api/shows/${showId}/reports/summary`, {
      method: "GET",
    });
  }

  getReportResults(showId: string) {
    return this.request<ReportResultsOutput>(`/api/shows/${showId}/reports/results`, {
      method: "GET",
    });
  }

  getReportAwards(showId: string) {
    return this.request<ReportAwardsOutput>(`/api/shows/${showId}/reports/awards`, {
      method: "GET",
    });
  }

  getReportClubParticipation(showId: string, options?: { includeUnknown?: boolean }) {
    const params = new URLSearchParams();
    if (options?.includeUnknown !== undefined) {
      params.set("includeUnknown", String(options.includeUnknown));
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ReportClubParticipationOutput>(
      `/api/shows/${showId}/reports/club-participation${suffix}`,
      {
        method: "GET",
      },
    );
  }

  getReportAwardCards(showId: string) {
    return this.request<ReportAwardCardsOutput>(
      `/api/shows/${showId}/reports/awards/cards`,
      {
        method: "GET",
      },
    );
  }

  getReportAwardsEmailPreview(showId: string, exhibitorId: string) {
    const params = new URLSearchParams();
    params.set("exhibitorId", exhibitorId);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<ReportAwardsEmailPreviewOutput>(
      `/api/shows/${showId}/reports/awards/email-preview${suffix}`,
      {
        method: "GET",
      },
    );
  }

  sendReportAwardsEmails(showId: string, payload?: SendReportAwardsEmailInput) {
    const body = payload ? JSON.stringify(payload) : undefined;
    return this.request<SendReportAwardsEmailOutput>(
      `/api/shows/${showId}/reports/awards/email`,
      {
        method: "POST",
        body,
      },
    );
  }

  getLeaderboard(showId: string) {
    return this.request<LeaderboardOutput>(`/api/reports/${showId}/leaderboard`, {
      method: "GET",
    });
  }

  getLeaderboardCsv(showId: string) {
    return this.fetchImpl(`${this.baseUrl}/api/reports/${showId}/leaderboard.csv`, {
      headers: this.defaultHeaders,
    }).then((res) => res.text());
  }

  listOrganizations() {
    return this.request<ListOrganizationsOutput>(`/api/orgs`, {
      method: "GET",
    });
  }

  getResultsCsv(showId: string) {
    return this.fetchImpl(`${this.baseUrl}/api/reports/${showId}/results.csv`, {
      headers: this.defaultHeaders,
    }).then((res) => res.text());
  }

  getFinanceCsv(showId: string) {
    return this.fetchImpl(`${this.baseUrl}/api/reports/${showId}/finance.csv`, {
      headers: this.defaultHeaders,
    }).then((res) => res.text());
  }

  async getJudgeSpeakingReportPdf(
    showId: string,
  ): Promise<{ buffer: ArrayBuffer; filename: string | null }> {
    let res: Response;
    try {
      const headers = new Headers(this.defaultHeaders ?? undefined);
      headers.set('accept', 'application/pdf');
      res = await this.fetchImpl(`${this.baseUrl}/api/reports/${showId}/judge-speaking.pdf`, {
        method: "GET",
        headers,
      });
    } catch (error) {
      const details = error instanceof Error ? error.message : String(error);
      throw new Error(`Unable to reach API at ${this.baseUrl}: ${details}`);
    }

    if (!res.ok) throw await apiErrorFromResponse(res);

    const disposition = res.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : null;
    const buffer = await res.arrayBuffer();
    return { buffer, filename };
  }

  async generateEntrantPacket(payload: EntrantPacketInput): Promise<EntrantPacketDownload> {
    let res: Response;
    try {
      const headers = new Headers(this.defaultHeaders ?? undefined);
      headers.set('content-type', 'application/json');
      headers.set('accept', 'application/pdf');
      res = await this.fetchImpl(`${this.baseUrl}/api/reports/entrant-packet`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
    } catch (error) {
      const details = error instanceof Error ? error.message : String(error);
      throw new Error(`Unable to reach API at ${this.baseUrl}: ${details}`);
    }

    if (!res.ok) throw await apiErrorFromResponse(res);

    const disposition = res.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : null;

    const entryCountHeader = res.headers.get('x-entrant-entry-count');
    const parsedCount = entryCountHeader ? Number.parseInt(entryCountHeader, 10) : Number.NaN;
    const entryCount = Number.isNaN(parsedCount) ? null : parsedCount;

    const buffer = await res.arrayBuffer();
    return { buffer, filename, entryCount };
  }
}
